import React, { useEffect, useState, useRef } from 'react';
import {
  Building2, Plus, Loader2, RefreshCw, Search, Check, X, Ban, Archive, Globe,
  ShieldAlert, Edit2, Trash2, Shield, Users, UserPlus, SlidersHorizontal, Settings,
  HelpCircle, Activity, Mail, FileText, Database, Calendar, Briefcase, ChevronDown,
  MoreVertical, Copy, Upload, ExternalLink, CheckCircle2, AlertTriangle, XCircle,
  UserCheck, ShieldCheck, UserCog, UserX, Ticket, Clock, CheckSquare, Layers
} from 'lucide-react';
import { safeFetch } from '../../lib/api';
import { workspaceSubdomainHost, getRootDomain } from '../../lib/platformDomain';
import { notifyToast } from '../../lib/toastStore';
import { confirmDialog } from '../../lib/confirmStore';
import Modal from '../shared/Modal';
import CustomRolePermissionsEditor, { createDefaultNoAccessPermissions } from './CustomRolePermissionsEditor';

interface Workspace {
  workspaceId: string;
  slug: string;
  /** The workspace's permanent original slug (never changes) and every
   * slug it has previously used as primary — all keep resolving. */
  originalSlug?: string;
  slugAliases?: string[];
  name: string;
  orgName: string;
  status: string;
  plan: string;
  country?: string;
  timezone?: string;
  customDomains?: string[];
  primaryAdminUid?: string;
  createdAt?: string;
  updatedAt?: string;
  branding?: {
    logoUrl?: string;
    primary?: string;
    accent?: string;
  };
}

interface WorkspaceStats {
  totalUsers: number;
  activeUsers: number;
  pendingInvites: number;
  disabledUsers: number;
  superAdmins: number;
  workspaceAdmins: number;
  customRoleUsers: number;
  employers: number;
  sponsors: number;
  referrers: number;
  clients: number;
  applicants: number;
  teams: number;
  departments: number;
  openSupportTickets: number;
  jobs: number;
  applications: number;
  documents: number;
  events: number;
  storageUsed: number;
  monthlyEmailVolume: number;
  monthlyLoginCount: number;
}

interface CustomRole {
  roleId: string;
  name: string;
  description: string;
  assignedCount: number;
  permissionCount: number;
  status: 'Active' | 'Archived';
  permissions: Record<string, string>;
  archived?: boolean;
}

interface WorkspaceHealth {
  emailConfigured: 'Healthy' | 'Warning' | 'Critical';
  smtpStatus: 'Healthy' | 'Warning' | 'Critical';
  domainVerified: 'Healthy' | 'Warning' | 'Critical';
  sslStatus: 'Healthy' | 'Warning' | 'Critical';
  storageUsage: 'Healthy' | 'Warning' | 'Critical';
  aiUsage: 'Healthy' | 'Warning' | 'Critical';
  loginActivity: 'Healthy' | 'Warning' | 'Critical';
  securityAlerts: 'Healthy' | 'Warning' | 'Critical';
}

interface WorkspaceUser {
  uid: string;
  email: string;
  name: string;
  role: string;
  status: 'active' | 'disabled';
  lastLoginAt?: string | null;
  profilePhotoUrl?: string;
}

interface AuditLog {
  id: string;
  action: string;
  adminName: string;
  details: string;
  timestamp: string;
  changedBy?: string;
  previousName?: string;
  newName?: string;
}

const STATUS_COLORS: Record<string, string> = {
  active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  suspended: 'bg-amber-50 text-amber-700 border-amber-200',
  pending: 'bg-blue-50 text-blue-700 border-blue-200',
  archived: 'bg-slate-100 text-slate-500 border-slate-200',
};

const PLANS = ['free', 'starter', 'growth', 'scale', 'enterprise'];
const MAX_CUSTOM_ROLES = 5;

export default function WorkspaceManagement() {
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [stats, setStats] = useState<Record<string, WorkspaceStats>>({});
  const [roles, setRoles] = useState<Record<string, CustomRole[]>>({});
  const [healths, setHealths] = useState<Record<string, WorkspaceHealth>>({});
  const [auditLogs, setAuditLogs] = useState<Record<string, AuditLog[]>>({});
  const [workspaceUsers, setWorkspaceUsers] = useState<Record<string, WorkspaceUser[]>>({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  // Sorting & Filtering
  const [statusFilter, setStatusFilter] = useState('all');
  const [planFilter, setPlanFilter] = useState('all');
  const [countryFilter, setCountryFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest'); // alphabetical, newest, oldest, most_active, largest
  const [showFilters, setShowFilters] = useState(false);

  // Expanded card slug
  const [expanded, setExpanded] = useState<string | null>(null);

  // Modals state
  const [activeWorkspace, setActiveWorkspace] = useState<Workspace | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showRename, setShowRename] = useState(false);
  const [showSlug, setShowSlug] = useState(false);
  const [slugValue, setSlugValue] = useState('');
  const [slugError, setSlugError] = useState('');
  const [showLogo, setShowLogo] = useState(false);
  const [showUsers, setShowUsers] = useState(false);
  const [showRoles, setShowRoles] = useState(false);
  const [showTeamMemberPermissions, setShowTeamMemberPermissions] = useState(false);
  const [teamMemberPermissions, setTeamMemberPermissions] = useState<Record<string, string>>({});
  const [showAudit, setShowAudit] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showBillingInfo, setShowBillingInfo] = useState(false);

  // Quick Action Dropdown State
  const [openDropdownSlug, setOpenDropdownSlug] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Forms state
  const [createForm, setCreateForm] = useState({ name: '', orgName: '', adminEmail: '', country: '', timezone: 'UTC', plan: 'growth' });
  const [createError, setCreateError] = useState('');
  const [createWarnings, setCreateWarnings] = useState<string[]>([]);
  const [renameValue, setRenameValue] = useState('');
  const [saving, setSaving] = useState(false);

  // HTML5 Canvas Logo Cropper State
  const [logoSourceUrl, setLogoSourceUrl] = useState<string | null>(null);
  const [cropZoom, setCropZoom] = useState(1);
  const [cropX, setCropX] = useState(0);
  const [cropY, setCropY] = useState(0);
  const [croppedBase64, setCroppedBase64] = useState<string | null>(null);
  const cropperImgRef = useRef<HTMLImageElement>(null);
  const cropperCanvasRef = useRef<HTMLCanvasElement>(null);

  // Manage Users state
  const [userSearch, setUserSearch] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');

  // Manage Roles state
  const [editingRole, setEditingRole] = useState<CustomRole | null>(null);
  const [showRoleForm, setShowRoleForm] = useState(false);
  const [roleForm, setRoleForm] = useState({
    name: '',
    description: '',
    permissions: createDefaultNoAccessPermissions() as Record<string, string>
  });

  // Delete confirmation text match
  const [deleteMatchInput, setDeleteMatchInput] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const data = await safeFetch('/api/platform/workspaces');
      const loadedWorkspaces = data.workspaces || [];
      setWorkspaces(loadedWorkspaces);

      // Lazily fetch stats for all workspaces in parallel background tasks
      loadedWorkspaces.forEach((w: Workspace) => {
        loadStatsForWorkspace(w.workspaceId, w.slug);
      });
    } catch (e: any) {
      notifyToast('error', 'Failed to load workspaces: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // Close dropdown on outside click
    const handleOutsideClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpenDropdownSlug(null);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  const loadStatsForWorkspace = async (wsId: string, slug?: string) => {
    const fetchKey = slug || wsId;
    if (!fetchKey) return;
    try {
      const data = await safeFetch(`/api/platform/workspaces/${fetchKey}/stats`);
      const updateMap = (prev: any, val: any) => ({
        ...prev,
        [wsId]: val,
        ...(slug ? { [slug]: val } : {}),
      });
      if (data.stats) setStats(prev => updateMap(prev, data.stats));
      if (data.customRoles) setRoles(prev => updateMap(prev, data.customRoles));
      if (data.health) setHealths(prev => updateMap(prev, data.health));
      if (data.auditLogs) setAuditLogs(prev => updateMap(prev, data.auditLogs));
      if (data.users) setWorkspaceUsers(prev => updateMap(prev, data.users));
    } catch { /* ignore */ }
  };

  const handleExpandToggle = (wsId: string, slug?: string) => {
    const next = expanded === wsId ? null : wsId;
    setExpanded(next);
    if (next) {
      loadStatsForWorkspace(wsId, slug);
    }
  };

  const openTeamMemberPermissions = async () => {
    if (!activeWorkspace) return;
    try {
      const data = await safeFetch(`/api/platform/workspaces/${activeWorkspace.slug}/team-member-permissions`);
      setTeamMemberPermissions(data.permissions || createDefaultNoAccessPermissions());
      setShowTeamMemberPermissions(true);
    } catch (e: any) {
      notifyToast('error', 'Failed to load Team Member permissions: ' + e.message);
    }
  };

  const saveTeamMemberPermissions = async () => {
    if (!activeWorkspace) return;
    setSaving(true);
    try {
      await safeFetch(`/api/platform/workspaces/${activeWorkspace.slug}/team-member-permissions`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ permissions: teamMemberPermissions }),
      });
      setShowTeamMemberPermissions(false);
      notifyToast('success', 'Team Member permissions saved. Members receive the new permissions immediately on their next request.');
    } catch (e: any) {
      notifyToast('error', 'Failed to save Team Member permissions: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  // Status/Plan update
  const updateWorkspaceProperty = async (slug: string, payload: Record<string, any>) => {
    try {
      await safeFetch(`/api/platform/workspaces/${slug}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      await load();
    } catch (e: any) {
      notifyToast('error', 'Failed to update workspace: ' + e.message);
    }
  };

  // Create Workspace
  const closeCreateDialog = () => {
    setShowCreate(false);
    setCreateError('');
    setCreateWarnings([]);
  };

  const createWorkspace = async () => {
    if (!createForm.name.trim()) { setCreateError('Workspace name is required.'); return; }
    setSaving(true);
    setCreateError('');
    try {
      const data: any = await safeFetch('/api/platform/workspaces', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(createForm),
      });
      const warnings: string[] = Array.isArray(data?.warnings) ? data.warnings : [];
      setCreateForm({ name: '', orgName: '', adminEmail: '', country: '', timezone: 'UTC', plan: 'growth' });
      await load();
      if (warnings.length) {
        // The workspace WAS created, but with caveats (e.g. its Identity
        // Platform tenant is still pending): keep the dialog open so the
        // operator actually sees them, with a Done button to dismiss.
        setCreateWarnings(warnings);
      } else {
        closeCreateDialog();
      }
    } catch (e: any) {
      setCreateError(e.message || 'Failed to create workspace.');
    } finally {
      setSaving(false);
    }
  };

  // Rename Workspace
  const handleRenameWorkspace = async () => {
    if (!activeWorkspace || !renameValue.trim()) return;
    setSaving(true);
    try {
      await safeFetch(`/api/platform/workspaces/${activeWorkspace.slug}/rename`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: renameValue.trim() })
      });
      setShowRename(false);
      await load();
      loadStatsForWorkspace(activeWorkspace.slug);
    } catch (e: any) {
      notifyToast('error', 'Failed to rename workspace: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  // Change Workspace Slug (default subdomain)
  const handleChangeSlug = async () => {
    if (!activeWorkspace || !slugValue.trim()) return;
    setSaving(true);
    setSlugError('');
    try {
      await safeFetch(`/api/platform/workspaces/${activeWorkspace.slug}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slug: slugValue.trim().toLowerCase() })
      });
      setShowSlug(false);
      setExpanded(null);
      await load();
      loadStatsForWorkspace(slugValue.trim().toLowerCase());
    } catch (e: any) {
      // Inline, in-dialog error: the server's refusal reason (e.g. slug
      // already taken / reserved) must be VISIBLE next to the field. The
      // previous alert()-only feedback left the dialog sitting open with
      // no explanation when the change was refused (silent failure).
      setSlugError(e.message || 'Failed to set primary subdomain. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Logo Cropping Management (HTML5 Canvas-based cropping)
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowed = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/svg+xml'];
    if (!allowed.includes(file.type)) {
      notifyToast('warning', 'Unsupported file format. Supported formats: PNG, JPG, WEBP, SVG.');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setLogoSourceUrl(reader.result as string);
      setCropZoom(1);
      setCropX(0);
      setCropY(0);
    };
    reader.readAsDataURL(file);
  };

  // Execute actual cropping on HTML5 Canvas
  useEffect(() => {
    if (!logoSourceUrl || !cropperImgRef.current || !cropperCanvasRef.current) return;

    const img = cropperImgRef.current;
    const canvas = cropperCanvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    img.onload = () => {
      // Define a standard output crop size of 256x256
      const destSize = 256;
      canvas.width = destSize;
      canvas.height = destSize;

      ctx.clearRect(0, 0, destSize, destSize);

      // Source rectangle dimensions (center-crop square fallback)
      const minOrigDim = Math.min(img.naturalWidth, img.naturalHeight);
      const srcSize = minOrigDim / cropZoom;

      // Adjust focal coordinates with pan sliders (offset bounds)
      const maxScrollX = (img.naturalWidth - srcSize) / 2;
      const maxScrollY = (img.naturalHeight - srcSize) / 2;
      const sx = ((img.naturalWidth - srcSize) / 2) + (cropX * maxScrollX);
      const sy = ((img.naturalHeight - srcSize) / 2) + (cropY * maxScrollY);

      ctx.drawImage(img, sx, sy, srcSize, srcSize, 0, 0, destSize, destSize);
      setCroppedBase64(canvas.toDataURL('image/png'));
    };

    // Trigger update if image already loaded
    if (img.complete) {
      img.onload(new Event('load'));
    }
  }, [logoSourceUrl, cropZoom, cropX, cropY]);

  const handleLogoUpload = async () => {
    if (!activeWorkspace || !croppedBase64) return;
    setSaving(true);
    try {
      const base64Str = croppedBase64.split(',')[1];
      await safeFetch(`/api/platform/workspaces/${activeWorkspace.slug}/branding/upload`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'logo',
          fileBase64: base64Str,
          contentType: 'image/png'
        })
      });
      setShowLogo(false);
      setLogoSourceUrl(null);
      setCroppedBase64(null);
      await load();
      loadStatsForWorkspace(activeWorkspace.slug);
    } catch (e: any) {
      notifyToast('error', 'Failed to upload logo: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleLogoRemove = async () => {
    if (!activeWorkspace) return;
    setSaving(true);
    try {
      await safeFetch(`/api/platform/workspaces/${activeWorkspace.slug}/branding/logo`, {
        method: 'DELETE'
      });
      setShowLogo(false);
      setLogoSourceUrl(null);
      setCroppedBase64(null);
      await load();
      loadStatsForWorkspace(activeWorkspace.slug);
    } catch (e: any) {
      notifyToast('error', 'Failed to remove logo: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  // User Actions
  const handleToggleUserBlock = async (user: WorkspaceUser) => {
    if (!activeWorkspace) return;
    const nextBlocked = user.status === 'active';
    try {
      await safeFetch(`/api/platform/workspaces/${activeWorkspace.slug}/users/${user.uid}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ blocked: nextBlocked })
      });
      loadStatsForWorkspace(activeWorkspace.slug);
    } catch (e: any) {
      notifyToast('error', 'Failed to change user status: ' + e.message);
    }
  };

  const handleUpdateUserRole = async (uid: string, newRole: string) => {
    if (!activeWorkspace) return;
    try {
      await safeFetch(`/api/platform/workspaces/${activeWorkspace.slug}/users/${uid}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role: newRole })
      });
      loadStatsForWorkspace(activeWorkspace.slug);
    } catch (e: any) {
      notifyToast('error', 'Failed to update user role: ' + e.message);
    }
  };

  const handleInviteUser = async () => {
    if (!activeWorkspace || !inviteEmail.trim()) return;
    try {
      await safeFetch(`/api/platform/workspaces/${activeWorkspace.slug}/users/invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: inviteEmail.trim(), role: inviteRole })
      });
      setInviteEmail('');
      notifyToast('success', 'Teammate invited successfully!');
      loadStatsForWorkspace(activeWorkspace.slug);
    } catch (e: any) {
      notifyToast('error', 'Failed to invite teammate: ' + e.message);
    }
  };

  // Role Actions
  const handleSaveRole = async () => {
    if (!activeWorkspace || !roleForm.name.trim()) return;
    setSaving(true);
    try {
      if (editingRole) {
        // Edit role
        await safeFetch(`/api/platform/workspaces/${activeWorkspace.slug}/roles/${editingRole.roleId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: roleForm.name,
            description: roleForm.description,
            permissions: roleForm.permissions
          })
        });
      } else {
        // Create role
        await safeFetch(`/api/platform/workspaces/${activeWorkspace.slug}/roles`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: roleForm.name,
            description: roleForm.description,
            permissions: roleForm.permissions
          })
        });
      }
      setShowRoleForm(false);
      setEditingRole(null);
      setRoleForm({
        name: '',
        description: '',
        permissions: {
          applicants: 'none', finance: 'none', jobs: 'none', employers: 'none', referrers: 'none',
          sponsors: 'none', organizations: 'none', batch: 'none', events: 'none', reports: 'none',
          approvals: 'none', settings: 'none', templates: 'none', 'doc-templates': 'none',
          users: 'none', tickets: 'none', 'ai-suggestions': 'none', invitations: 'none'
        }
      });
      loadStatsForWorkspace(activeWorkspace.slug);
    } catch (e: any) {
      notifyToast('error', 'Failed to save role: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDuplicateRole = async (role: CustomRole) => {
    if (!activeWorkspace) return;
    if (!(await confirmDialog(`Duplicate the role "${role.name}"? A new role named "${role.name} Copy" will be created with the same permissions.`))) return;
    try {
      await safeFetch(`/api/platform/workspaces/${activeWorkspace.slug}/roles`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: `${role.name} Copy`,
          description: role.description || `Duplicate of ${role.name}`,
          permissions: role.permissions
        })
      });
      loadStatsForWorkspace(activeWorkspace.slug);
    } catch (e: any) {
      notifyToast('error', 'Failed to duplicate role: ' + e.message);
    }
  };

  const handleDeleteRole = async (roleId: string) => {
    if (!activeWorkspace) return;
    const roleToDelete = (roles[activeWorkspace.workspaceId] || roles[activeWorkspace.slug] || []).find(r => r.roleId === roleId);
    const deleteMessage = roleToDelete
      ? `Are you sure you want to delete the custom role "${roleToDelete.name}"? This will archive the role.`
      : 'Are you sure you want to delete this custom role? This will archive the role.';
    if (!(await confirmDialog(deleteMessage))) return;
    try {
      await safeFetch(`/api/platform/workspaces/${activeWorkspace.slug}/roles/${roleId}`, {
        method: 'DELETE'
      });
      loadStatsForWorkspace(activeWorkspace.slug);
    } catch (e: any) {
      notifyToast('error', 'Failed to delete role: ' + e.message);
    }
  };

  // Delete Workspace (protected)
  const handleDeleteWorkspace = async () => {
    if (!activeWorkspace || deleteMatchInput !== 'DELETE') {
      notifyToast('warning', 'Confirmation word does not match.');
      return;
    }
    setSaving(true);
    try {
      await safeFetch(`/api/platform/workspaces/${activeWorkspace.slug}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'archived' }) // Safe soft-delete archiving
      });
      setShowDeleteConfirm(false);
      setDeleteMatchInput('');
      await load();
      notifyToast('success', 'Workspace successfully archived and disabled.');
    } catch (e: any) {
      notifyToast('error', 'Failed to delete workspace: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  // Helper format storage
  const formatStorage = (bytes: number) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Generates initials for the avatar if no logo URL is provided
  const getInitials = (name: string) => {
    return name ? name.split(' ').slice(0, 2).map(p => p[0]).join('').toUpperCase() : 'WS';
  };

  // Extraction of unique countries from loaded workspaces
  const countries = Array.from(new Set(workspaces.map(w => w.country).filter(Boolean))) as string[];

  // Perform client side search & filtering
  const filteredWorkspaces = workspaces.filter(w => {
    // Search query
    const matchSearch = !search.trim() ||
      w.name.toLowerCase().includes(search.toLowerCase()) ||
      w.orgName?.toLowerCase().includes(search.toLowerCase()) ||
      w.slug.toLowerCase().includes(search.toLowerCase()) ||
      w.workspaceId.toLowerCase().includes(search.toLowerCase()) ||
      w.plan.toLowerCase().includes(search.toLowerCase()) ||
      w.customDomains?.some(d => d.toLowerCase().includes(search.toLowerCase()));

    // Status Filter
    const matchStatus = statusFilter === 'all' || w.status === statusFilter;

    // Plan Filter
    const matchPlan = planFilter === 'all' || w.plan === planFilter;

    // Country Filter
    const matchCountry = countryFilter === 'all' || w.country === countryFilter;

    return matchSearch && matchStatus && matchPlan && matchCountry;
  });

  // Client side sorting
  const sortedWorkspaces = [...filteredWorkspaces].sort((a, b) => {
    if (sortBy === 'alphabetical') {
      return a.name.localeCompare(b.name);
    }
    if (sortBy === 'newest') {
      return String(b.createdAt || '').localeCompare(String(a.createdAt || ''));
    }
    if (sortBy === 'oldest') {
      return String(a.createdAt || '').localeCompare(String(b.createdAt || ''));
    }
    if (sortBy === 'most_active') {
      const activeA = stats[a.slug]?.monthlyLoginCount ?? 0;
      const activeB = stats[b.slug]?.monthlyLoginCount ?? 0;
      return activeB - activeA;
    }
    if (sortBy === 'largest') {
      const sizeA = stats[a.slug]?.totalUsers ?? 0;
      const sizeB = stats[b.slug]?.totalUsers ?? 0;
      return sizeB - sizeA;
    }
    return 0;
  });

  return (
    <div className="space-y-6">
      {/* Platform Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap bg-white/70 backdrop-blur-md p-6 rounded-3xl border border-slate-100 shadow-sm">
        <div className="flex items-center gap-4">
          <div className="h-12 w-12 rounded-2xl bg-gradient-to-tr from-brand-primary to-brand-accent flex items-center justify-center shadow-md">
            <Layers className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-black text-slate-900 tracking-tight">Workspace Console</h1>
            <p className="text-xs text-slate-500 font-medium">Enterprise administrator dashboard to monitor, manage and scale tenant workspaces.</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button onClick={load} className="p-2.5 rounded-xl hover:bg-slate-50 text-slate-500 border border-slate-200 bg-white shadow-sm transition-all cursor-pointer" title="Refresh Live Data">
            <RefreshCw className="h-4 w-4" />
          </button>
          <button onClick={() => setShowCreate(true)} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl btn-primary text-white text-xs font-bold shadow-md shadow-brand-primary/10 hover:shadow-lg transition-all cursor-pointer">
            <Plus className="h-4 w-4" /> New Workspace
          </button>
        </div>
      </div>

      {/* Stats bar */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Workspaces', value: workspaces.length, icon: Building2, color: 'text-indigo-600 bg-indigo-50 border-indigo-100' },
          { label: 'Active Subscriptions', value: workspaces.filter(w => w.status === 'active').length, icon: UserCheck, color: 'text-emerald-600 bg-emerald-50 border-emerald-100' },
          { label: 'Enterprise Tenants', value: workspaces.filter(w => w.plan === 'enterprise').length, icon: ShieldCheck, color: 'text-amber-600 bg-amber-50 border-amber-100' },
          { label: 'Suspended/Archived', value: workspaces.filter(w => w.status === 'suspended' || w.status === 'archived').length, icon: UserX, color: 'text-rose-600 bg-rose-50 border-rose-100' },
        ].map(card => (
          <div key={card.label} className="bg-white border border-slate-100 p-5 rounded-3xl shadow-sm flex items-center justify-between gap-4">
            <div>
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{card.label}</p>
              <p className="text-2xl font-black text-slate-900 mt-1">{card.value}</p>
            </div>
            <div className={`p-3 rounded-2xl border ${card.color}`}>
              <card.icon className="h-5 w-5" />
            </div>
          </div>
        ))}
      </div>

      {/* Advanced search, sort & filters */}
      <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-4 space-y-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[260px]">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search workspaces by name, ID, domain or plan..."
              className="pl-10 pr-4 py-2.5 bg-slate-50/50 border border-slate-200 rounded-2xl text-sm w-full focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:bg-white transition-all placeholder:text-slate-400 text-slate-800"
            />
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`inline-flex items-center gap-1.5 px-3.5 py-2.5 rounded-xl border text-xs font-bold transition-all cursor-pointer ${
                showFilters || statusFilter !== 'all' || planFilter !== 'all' || countryFilter !== 'all'
                  ? 'border-brand-primary bg-brand-primary/5 text-brand-primary'
                  : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
              }`}
            >
              <SlidersHorizontal className="h-4 w-4" />
              Filters {(statusFilter !== 'all' || planFilter !== 'all' || countryFilter !== 'all') && '•'}
            </button>

            <select
              value={sortBy}
              onChange={e => setSortBy(e.target.value)}
              className="border border-slate-200 rounded-xl px-3 py-2.5 text-xs font-bold bg-white text-slate-600 outline-none hover:bg-slate-50 cursor-pointer"
            >
              <option value="newest">Sort: Newest</option>
              <option value="oldest">Sort: Oldest</option>
              <option value="alphabetical">Sort: Alphabetical</option>
              <option value="most_active">Sort: Most Active</option>
              <option value="largest">Sort: Largest Workspace</option>
            </select>
          </div>
        </div>

        {/* Expandable filtering block */}
        {showFilters && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-3 border-t border-slate-100 animate-fadeIn">
            <div>
              <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">Workspace Status</label>
              <select
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value)}
                className="w-full border border-slate-200 rounded-xl px-3 py-2 text-xs bg-white text-slate-600 outline-none"
              >
                <option value="all">All Statuses</option>
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
                <option value="archived">Archived</option>
              </select>
            </div>

            <div>
              <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">Subscription Plan</label>
              <select
                value={planFilter}
                onChange={e => setPlanFilter(e.target.value)}
                className="w-full border border-slate-200 rounded-xl px-3 py-2 text-xs bg-white text-slate-600 outline-none"
              >
                <option value="all">All Plans</option>
                {PLANS.map(p => <option key={p} value={p}>{p.toUpperCase()}</option>)}
              </select>
            </div>

            <div>
              <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">Country / Territory</label>
              <select
                value={countryFilter}
                onChange={e => setCountryFilter(e.target.value)}
                className="w-full border border-slate-200 rounded-xl px-3 py-2 text-xs bg-white text-slate-600 outline-none"
              >
                <option value="all">All Countries</option>
                {countries.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Workspaces list */}
      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map(n => (
            <div key={n} className="bg-white rounded-3xl border border-slate-100 p-6 space-y-4 shadow-sm skeleton animate-pulse">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-2xl bg-slate-100" />
                <div className="space-y-2 flex-1">
                  <div className="h-4 bg-slate-100 rounded w-1/4" />
                  <div className="h-3 bg-slate-100 rounded w-1/2" />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          {sortedWorkspaces.length === 0 && (
            <div className="bg-white rounded-3xl border border-slate-100 p-12 text-center text-slate-400">
              <Building2 className="h-10 w-10 mx-auto text-slate-300 stroke-1 mb-3" />
              <p className="text-sm font-bold text-slate-500">No workspaces matching filters.</p>
              <p className="text-xs text-slate-500 mt-1">Try resetting the search query or filters.</p>
            </div>
          )}

          {sortedWorkspaces.map(w => {
            const workspaceStatsData = stats[w.workspaceId] || stats[w.slug];
            const isExpanded = expanded === w.workspaceId || expanded === w.slug;
            const logoUrl = w.branding?.logoUrl;
            const customDomainStr = w.customDomains?.join(', ');
            const wsRolesList = roles[w.workspaceId] || roles[w.slug] || [];
            const wsHealthData = healths[w.workspaceId] || healths[w.slug];

            return (
              <div key={w.workspaceId} className={`bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden transition-all duration-200 hover:shadow-md ${isExpanded ? 'ring-1 ring-brand-primary/10' : ''}`}>

                {/* Workspace overview card head */}
                <div className="flex items-center justify-between gap-4 p-5 flex-wrap md:flex-nowrap">
                  <button onClick={() => handleExpandToggle(w.workspaceId, w.slug)} className="flex items-center gap-4 text-left flex-1 min-w-0 cursor-pointer">

                    {/* Workspace Logo Avatar */}
                    {logoUrl ? (
                      <img src={logoUrl} alt={w.name} className="h-12 w-12 rounded-2xl border border-slate-100 object-contain bg-slate-50 shrink-0" />
                    ) : (
                      <div className="h-12 w-12 rounded-2xl bg-brand-primary/5 text-brand-primary font-black flex items-center justify-center border border-brand-primary/10 text-sm shrink-0">
                        {getInitials(w.name)}
                      </div>
                    )}

                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-black text-slate-900 truncate">{w.orgName || w.name}</h3>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold border capitalize ${STATUS_COLORS[w.status] || 'bg-slate-50 border-slate-200'}`}>
                          {w.status}
                        </span>
                      </div>
                      <p className="text-[11px] text-slate-500 font-mono mt-0.5 truncate">
                        {workspaceSubdomainHost(w.slug)} {customDomainStr && `· ${customDomainStr}`}
                      </p>
                      <p className="text-[11px] text-slate-500 mt-0.5">ID: {w.workspaceId} · Created: {w.createdAt ? new Date(w.createdAt).toLocaleDateString() : '—'}</p>
                    </div>
                  </button>

                  <div className="flex items-center gap-4 shrink-0">
                    <div className="text-right hidden sm:block">
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Plan Level</p>
                      <span className="text-xs font-black text-slate-800 uppercase bg-slate-50 border border-slate-100 rounded-lg px-2 py-0.5 mt-0.5 inline-block">
                        {w.plan}
                      </span>
                    </div>

                    <div className="text-right hidden md:block">
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Storage</p>
                      <span className="text-xs font-bold text-slate-600 mt-0.5 inline-block">
                        {workspaceStatsData ? formatStorage(workspaceStatsData.storageUsed) : '—'}
                      </span>
                    </div>

                    {/* Quick Action Button */}
                    <div className="relative">
                      <button aria-label="Workspace actions" title="Workspace actions"
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenDropdownSlug(openDropdownSlug === w.slug ? null : w.slug);
                          setActiveWorkspace(w);
                        }}
                        className="p-2 hover:bg-slate-50 border border-slate-100 rounded-xl text-slate-500 hover:text-slate-800 cursor-pointer"
                      >
                        <MoreVertical className="h-4 w-4" />
                      </button>

                      {/* Dropdown Menu */}
                      {openDropdownSlug === w.slug && (
                        <div ref={dropdownRef} className="absolute right-0 mt-2 w-52 bg-white rounded-2xl border border-slate-100 shadow-xl py-2 z-50 text-left animate-fadeIn">
                          <button onClick={() => { window.open(`https://${workspaceSubdomainHost(w.slug)}`, '_blank'); setOpenDropdownSlug(null); }} className="w-full text-left px-4 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 flex items-center gap-2 cursor-pointer">
                            <ExternalLink className="h-3.5 w-3.5 text-slate-400" /> View Portal
                          </button>
                          <button onClick={() => { setShowRename(true); setRenameValue(w.name); setOpenDropdownSlug(null); }} className="w-full text-left px-4 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 flex items-center gap-2 cursor-pointer">
                            <Edit2 className="h-3.5 w-3.5 text-slate-400" /> Rename Workspace
                          </button>
                          <button onClick={() => { setShowLogo(true); setLogoSourceUrl(null); setCroppedBase64(null); setOpenDropdownSlug(null); }} className="w-full text-left px-4 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 flex items-center gap-2 cursor-pointer">
                            <Upload className="h-3.5 w-3.5 text-slate-400" /> Manage Logo
                          </button>
                          <button onClick={() => { setShowUsers(true); setOpenDropdownSlug(null); }} className="w-full text-left px-4 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 flex items-center gap-2 cursor-pointer">
                            <Users className="h-3.5 w-3.5 text-slate-400" /> Manage Users
                          </button>
                          <button onClick={() => { setShowRoles(true); setOpenDropdownSlug(null); }} className="w-full text-left px-4 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 flex items-center gap-2 cursor-pointer">
                            <UserCog className="h-3.5 w-3.5 text-slate-400" /> Manage Roles
                          </button>
                          <button onClick={() => { setShowAudit(true); setOpenDropdownSlug(null); }} className="w-full text-left px-4 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 flex items-center gap-2 cursor-pointer">
                            <Clock className="h-3.5 w-3.5 text-slate-400" /> Audit Logs
                          </button>
                          <button onClick={() => { setShowBillingInfo(true); setOpenDropdownSlug(null); }} className="w-full text-left px-4 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 flex items-center gap-2 cursor-pointer">
                            <Database className="h-3.5 w-3.5 text-slate-400" /> Billing & Subscription
                          </button>

                          <div className="border-t border-slate-50 my-1"></div>

                          {w.status === 'active' ? (
                            <button onClick={() => { updateWorkspaceProperty(w.slug, { status: 'suspended' }); setOpenDropdownSlug(null); }} className="w-full text-left px-4 py-2 text-xs font-bold text-amber-700 hover:bg-amber-50 flex items-center gap-2 cursor-pointer">
                              <Ban className="h-3.5 w-3.5" /> Suspend Workspace
                            </button>
                          ) : (
                            <button onClick={() => { updateWorkspaceProperty(w.slug, { status: 'active' }); setOpenDropdownSlug(null); }} className="w-full text-left px-4 py-2 text-xs font-bold text-emerald-700 hover:bg-emerald-50 flex items-center gap-2 cursor-pointer">
                              <Check className="h-3.5 w-3.5" /> Activate Workspace
                            </button>
                          )}

                          <button onClick={() => { updateWorkspaceProperty(w.slug, { status: 'archived' }); setOpenDropdownSlug(null); }} className="w-full text-left px-4 py-2 text-xs font-bold text-slate-600 hover:bg-slate-50 flex items-center gap-2 cursor-pointer">
                            <Archive className="h-3.5 w-3.5" /> Archive Workspace
                          </button>
                          <button onClick={() => { setShowDeleteConfirm(true); setOpenDropdownSlug(null); }} className="w-full text-left px-4 py-2 text-xs font-bold text-rose-700 hover:bg-rose-50 flex items-center gap-2 cursor-pointer">
                            <Trash2 className="h-3.5 w-3.5" /> Delete Workspace
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Expanded Details Section */}
                {isExpanded && (
                  <div className="border-t border-slate-100 bg-slate-50/40 p-5 space-y-6 animate-fadeIn">

                    {/* Live Statistics Block */}
                    <div>
                      <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                        <Activity className="h-3.5 w-3.5 text-indigo-500" /> Live Workspace Statistics
                      </h4>
                      {!workspaceStatsData ? (
                        <div className="flex items-center gap-2 py-4 justify-center text-slate-400 text-xs font-bold">
                          <Loader2 className="h-4 w-4 animate-spin text-brand-primary" /> Loading metrics from Firestore...
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
                          {[
                            { label: 'Total Users', value: workspaceStatsData.totalUsers, icon: Users },
                            { label: 'Active Users', value: workspaceStatsData.activeUsers, icon: UserCheck },
                            { label: 'Disabled Users', value: workspaceStatsData.disabledUsers, icon: UserX },
                            { label: 'Pending Invites', value: workspaceStatsData.pendingInvites, icon: UserPlus },
                            { label: 'Super Admins', value: workspaceStatsData.superAdmins, icon: Shield },
                            { label: 'Workspace Admins', value: workspaceStatsData.workspaceAdmins, icon: ShieldCheck },
                            { label: 'Custom Roles', value: workspaceStatsData.customRoleUsers, icon: UserCog },
                            { label: 'Employers', value: workspaceStatsData.employers, icon: Briefcase },
                            { label: 'Sponsors', value: workspaceStatsData.sponsors, icon: ShieldCheck },
                            { label: 'Referrers', value: workspaceStatsData.referrers, icon: Users },
                            { label: 'Clients', value: workspaceStatsData.clients, icon: Users },
                            { label: 'Applicants', value: workspaceStatsData.applicants, icon: UserCheck },
                            { label: 'Teams', value: workspaceStatsData.teams, icon: Layers },
                            { label: 'Departments', value: workspaceStatsData.departments, icon: Layers },
                            { label: 'Open Tickets', value: workspaceStatsData.openSupportTickets, icon: Ticket },
                            { label: 'Jobs', value: workspaceStatsData.jobs, icon: Briefcase },
                            { label: 'Applications', value: workspaceStatsData.applications, icon: FileText },
                            { label: 'Documents', value: workspaceStatsData.documents, icon: FileText },
                            { label: 'Events', value: workspaceStatsData.events, icon: Calendar },
                            { label: 'Monthly Emails', value: workspaceStatsData.monthlyEmailVolume, icon: Mail },
                            { label: 'Monthly Logins', value: workspaceStatsData.monthlyLoginCount, icon: Clock },
                          ].map(statItem => (
                            <div key={statItem.label} className="bg-white border border-slate-100 p-3.5 rounded-2xl shadow-sm flex flex-col justify-between hover:border-slate-200 hover:-translate-y-0.5 transition-all">
                              <div className="flex items-center justify-between gap-1">
                                <span className="text-[11px] font-black text-slate-400 uppercase tracking-wider leading-none">{statItem.label}</span>
                                <statItem.icon className="h-3.5 w-3.5 text-slate-400" />
                              </div>
                              <span className="text-base font-black text-slate-800 mt-2 leading-none">{statItem.value}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Workspace User Breakdown Block */}
                    {workspaceStatsData && (
                      <div className="bg-white border border-slate-100 p-5 rounded-3xl shadow-sm space-y-3">
                        <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                          <Users className="h-3.5 w-3.5 text-indigo-500" /> Detailed User Breakdown
                        </h4>
                        <div className="flex flex-wrap gap-4 text-xs font-bold text-slate-600">
                          <div className="border-r border-slate-100 pr-4">
                            <span className="text-slate-400 block text-[11px] uppercase tracking-wider">Total</span>
                            <span className="text-sm font-black text-slate-800">{workspaceStatsData.totalUsers}</span>
                          </div>
                          <div className="border-r border-slate-100 pr-4">
                            <span className="text-slate-400 block text-[11px] uppercase tracking-wider">Super Admin</span>
                            <span className="text-sm font-black text-slate-800">{workspaceStatsData.superAdmins}</span>
                          </div>
                          <div className="border-r border-slate-100 pr-4">
                            <span className="text-slate-400 block text-[11px] uppercase tracking-wider">Workspace Admin</span>
                            <span className="text-sm font-black text-slate-800">{workspaceStatsData.workspaceAdmins}</span>
                          </div>
                          <div className="border-r border-slate-100 pr-4">
                            <span className="text-slate-400 block text-[11px] uppercase tracking-wider">Employers</span>
                            <span className="text-sm font-black text-slate-800">{workspaceStatsData.employers}</span>
                          </div>
                          <div className="border-r border-slate-100 pr-4">
                            <span className="text-slate-400 block text-[11px] uppercase tracking-wider">Sponsors</span>
                            <span className="text-sm font-black text-slate-800">{workspaceStatsData.sponsors}</span>
                          </div>
                          <div className="border-r border-slate-100 pr-4">
                            <span className="text-slate-400 block text-[11px] uppercase tracking-wider">Referrers</span>
                            <span className="text-sm font-black text-slate-800">{workspaceStatsData.referrers}</span>
                          </div>
                          <div className="border-r border-slate-100 pr-4">
                            <span className="text-slate-400 block text-[11px] uppercase tracking-wider">Clients</span>
                            <span className="text-sm font-black text-slate-800">{workspaceStatsData.clients}</span>
                          </div>
                          <div className="border-r border-slate-100 pr-4">
                            <span className="text-slate-400 block text-[11px] uppercase tracking-wider">Applicants</span>
                            <span className="text-sm font-black text-slate-800">{workspaceStatsData.applicants}</span>
                          </div>
                          {/* Automatically listing breakdowns for each custom role */}
                          {wsRolesList.map(r => (
                            <div key={r.roleId} className="border-r border-slate-100 pr-4 last:border-r-0">
                              <span className="text-slate-400 block text-[11px] uppercase tracking-wider truncate max-w-[100px]">{r.name}</span>
                              <span className="text-sm font-black text-slate-800">{r.assignedCount}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Workspace Health Section */}
                    <div>
                      <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                        <ShieldCheck className="h-3.5 w-3.5 text-emerald-500" /> Workspace Health & Security
                      </h4>
                      {(!wsHealthData) ? (
                        <div className="text-slate-400 text-xs font-bold">Loading health indicators...</div>
                      ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 bg-white border border-slate-100 p-4 rounded-3xl shadow-sm">
                          {[
                            { key: 'emailConfigured', label: 'Email Configuration' },
                            { key: 'smtpStatus', label: 'SMTP Services' },
                            { key: 'domainVerified', label: 'Domain Verification' },
                            { key: 'sslStatus', label: 'SSL Certificate' },
                            { key: 'storageUsage', label: 'Storage Allocation' },
                            { key: 'aiUsage', label: 'AI Prompt Limits' },
                            { key: 'loginActivity', label: 'Recent Activity' },
                            { key: 'securityAlerts', label: 'Security Threats' },
                          ].map(indicator => {
                            const val = wsHealthData[indicator.key as keyof WorkspaceHealth] || 'Healthy';
                            return (
                              <div key={indicator.key} className="flex items-center gap-2.5">
                                {val === 'Healthy' && <span className="h-2.5 w-2.5 rounded-full bg-emerald-500 shadow-sm shadow-emerald-500/50" />}
                                {val === 'Warning' && <span className="h-2.5 w-2.5 rounded-full bg-amber-500 shadow-sm shadow-amber-500/50" />}
                                {val === 'Critical' && <span className="h-2.5 w-2.5 rounded-full bg-rose-500 shadow-sm shadow-rose-500/50" />}
                                <div>
                                  <p className="text-[10px] font-bold text-slate-500 leading-tight">{indicator.label}</p>
                                  <p className="text-[11px] font-black text-slate-400 uppercase leading-none mt-0.5">{val}</p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {/* Workspace Info Summary Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="bg-white border border-slate-100 p-4 rounded-3xl shadow-sm space-y-2">
                        <h5 className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Workspace Properties</h5>
                        <div className="grid grid-cols-2 gap-y-2 text-xs font-semibold text-slate-600">
                          <span>Primary Subdomain:</span>
                          <span className="font-mono text-slate-800 flex items-center gap-1.5 min-w-0">
                            <span className="truncate">{workspaceSubdomainHost(w.slug)}</span>
                            <button onClick={() => { setActiveWorkspace(w); setSlugValue(w.slug); setSlugError(''); setShowSlug(true); }} className="text-slate-400 hover:text-brand shrink-0 cursor-pointer" aria-label="Change primary subdomain" title="Change primary subdomain"><Edit2 className="h-3.5 w-3.5" /></button>
                          </span>
                          <span>Time Zone:</span>
                          <span className="text-slate-800">{w.timezone || 'UTC'}</span>
                          <span>Country:</span>
                          <span className="text-slate-800">{w.country || 'Not Configured'}</span>
                          <span>Custom Domain:</span>
                          <span className="font-mono text-slate-800 truncate">{w.customDomains?.length ? w.customDomains.join(', ') : 'None'}</span>
                        </div>
                      </div>

                      <div className="bg-white border border-slate-100 p-4 rounded-3xl shadow-sm space-y-2">
                        <h5 className="text-[11px] font-black text-slate-400 uppercase tracking-widest font-sans">Super-Admin Actions</h5>
                        <div className="flex gap-2 flex-wrap">
                          <button onClick={() => { setActiveWorkspace(w); setShowUsers(true); }} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-slate-200 text-slate-700 text-xs font-bold hover:bg-slate-50 cursor-pointer">
                            <Users className="h-3.5 w-3.5 text-slate-400" /> Manage Users
                          </button>
                          <button onClick={() => { setActiveWorkspace(w); setShowRoles(true); }} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-slate-200 text-slate-700 text-xs font-bold hover:bg-slate-50 cursor-pointer">
                            <UserCog className="h-3.5 w-3.5 text-slate-400" /> Custom Roles
                          </button>
                          <button onClick={() => { setActiveWorkspace(w); setShowLogo(true); }} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-slate-200 text-slate-700 text-xs font-bold hover:bg-slate-50 cursor-pointer">
                            <Upload className="h-3.5 w-3.5 text-slate-400" /> Change Logo
                          </button>
                          <button onClick={() => { setActiveWorkspace(w); setShowAudit(true); }} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-slate-200 text-slate-700 text-xs font-bold hover:bg-slate-50 cursor-pointer">
                            <Clock className="h-3.5 w-3.5 text-slate-400" /> Audit Logs
                          </button>
                        </div>
                      </div>
                    </div>

                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* CREATE WORKSPACE MODAL */}
      {showCreate && (
        <Modal onClose={closeCreateDialog} title="Create New Workspace">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-6 space-y-4 animate-scaleIn">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-black text-slate-900 flex items-center gap-2">
                <Building2 className="h-5 w-5 text-brand-primary" /> Create New Workspace
              </h3>
              <button aria-label="Close" title="Close" onClick={closeCreateDialog} className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-400 cursor-pointer">
                <X className="h-4 w-4" />
              </button>
            </div>
            <p className="text-xs text-slate-500 font-medium">Provision a completely isolated multi-tenant workspace instantly with custom subdomain and brand settings.</p>

            {createError && (
              <div role="alert" className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs font-semibold text-red-700">
                <XCircle className="h-4 w-4 shrink-0 mt-px" />
                <span>{createError}</span>
              </div>
            )}

            {createWarnings.length > 0 && (
              <div role="alert" className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-px" />
                <div>
                  <p>Workspace created, with warnings:</p>
                  <ul className="list-disc pl-4 mt-1 space-y-0.5">
                    {createWarnings.map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                </div>
              </div>
            )}

            <div className="space-y-3">
              <div>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Workspace Name (Slug permanent)</p>
                <input
                  value={createForm.name}
                  onChange={v => setCreateForm(f => ({ ...f, name: v.target.value }))}
                  placeholder="acme"
                  className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand-primary/20 bg-slate-50/30"
                />
              </div>

              <div>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Organization Name</p>
                <input
                  value={createForm.orgName}
                  onChange={v => setCreateForm(f => ({ ...f, orgName: v.target.value }))}
                  placeholder="Acme Recruiting Inc."
                  className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand-primary/20 bg-slate-50/30"
                />
              </div>

              <div>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Primary Administrator Email</p>
                <input
                  value={createForm.adminEmail}
                  onChange={v => setCreateForm(f => ({ ...f, adminEmail: v.target.value }))}
                  placeholder="admin@acme.com"
                  className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand-primary/20 bg-slate-50/30"
                />
              </div>

              <div>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Country</p>
                <input
                  value={createForm.country}
                  onChange={v => setCreateForm(f => ({ ...f, country: v.target.value }))}
                  placeholder="United States"
                  className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand-primary/20 bg-slate-50/30"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Time Zone</p>
                  <select
                    value={createForm.timezone}
                    onChange={e => setCreateForm(f => ({ ...f, timezone: e.target.value }))}
                    className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm bg-white outline-none"
                  >
                    <option value="UTC">UTC</option>
                    <option value="America/New_York">America/New_York</option>
                    <option value="Europe/London">Europe/London</option>
                    <option value="Asia/Tokyo">Asia/Tokyo</option>
                  </select>
                </div>
                <div>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Plan</p>
                  <select
                    value={createForm.plan}
                    onChange={e => setCreateForm(f => ({ ...f, plan: e.target.value }))}
                    className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm bg-white outline-none"
                  >
                    {PLANS.map(p => <option key={p} value={p}>{p.toUpperCase()}</option>)}
                  </select>
                </div>
              </div>
            </div>

            {createWarnings.length > 0 ? (
              <div className="flex gap-2 pt-2">
                <button onClick={closeCreateDialog} className="flex-1 px-4 py-2.5 rounded-xl text-xs font-bold text-white btn-primary cursor-pointer flex items-center justify-center">
                  Done
                </button>
              </div>
            ) : (
              <div className="flex gap-2 pt-2">
                <button onClick={closeCreateDialog} className="flex-1 px-4 py-2.5 rounded-xl text-xs font-bold text-slate-600 bg-slate-50 border border-slate-100 hover:bg-slate-100 cursor-pointer">
                  Cancel
                </button>
                <button onClick={createWorkspace} disabled={saving} className="flex-1 px-4 py-2.5 rounded-xl text-xs font-bold text-white btn-primary disabled:opacity-50 cursor-pointer flex items-center justify-center">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create'}
                </button>
              </div>
            )}
          </div>
        </Modal>
      )}

      {/* RENAME WORKSPACE MODAL */}
      {showRename && activeWorkspace && (
        <Modal onClose={() => setShowRename(false)} title="Rename Workspace">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-sm p-6 space-y-4 animate-scaleIn">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-black text-slate-900 flex items-center gap-2">
                <Edit2 className="h-4 w-4 text-brand-primary" /> Rename Workspace
              </h3>
              <button aria-label="Close" title="Close" onClick={() => setShowRename(false)} className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-400 cursor-pointer">
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-3">
              <p className="text-xs text-slate-500 font-medium">Please enter a new display name for the workspace.</p>
              <div>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Previous Name</p>
                <input disabled value={activeWorkspace.name} className="w-full border border-slate-100 rounded-xl px-3 py-2 text-xs bg-slate-50 text-slate-400 font-bold" />
              </div>

              <div>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">New Workspace Name</p>
                <input
                  value={renameValue}
                  onChange={e => setRenameValue(e.target.value)}
                  placeholder="Enter new name"
                  className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand-primary/20 bg-slate-50/30"
                />
              </div>
            </div>

            <div className="flex gap-2 pt-1">
              <button onClick={() => setShowRename(false)} className="flex-1 px-4 py-2.5 rounded-xl text-xs font-bold text-slate-600 bg-slate-50 border border-slate-100 hover:bg-slate-100 cursor-pointer">
                Cancel
              </button>
              <button onClick={handleRenameWorkspace} disabled={saving} className="flex-1 px-4 py-2.5 rounded-xl text-xs font-bold text-white btn-primary disabled:opacity-50 cursor-pointer flex items-center justify-center">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Rename'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* CHANGE WORKSPACE SLUG MODAL */}
      {showSlug && activeWorkspace && (
        <Modal onClose={() => setShowSlug(false)} title="Change Primary Subdomain">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-sm p-6 space-y-4 animate-scaleIn">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-black text-slate-900 flex items-center gap-2">
                <Globe className="h-4 w-4 text-brand-primary" /> Change Primary Subdomain
              </h3>
              <button aria-label="Close" title="Close" onClick={() => setShowSlug(false)} className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-400 cursor-pointer">
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-3">
              <p className="text-xs text-slate-500 font-medium">Setting a new primary subdomain adds it to this workspace. The original subdomain — and every previous primary — keeps working permanently, so existing links never break.</p>
              <div>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Current Primary Slug</p>
                <input disabled value={activeWorkspace.slug} className="w-full border border-slate-100 rounded-xl px-3 py-2 text-xs bg-slate-50 text-slate-400 font-bold font-mono" />
              </div>
              {activeWorkspace.originalSlug && (
                <div>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Original Slug (permanent)</p>
                  <input disabled value={activeWorkspace.originalSlug} className="w-full border border-slate-100 rounded-xl px-3 py-2 text-xs bg-slate-50 text-slate-400 font-bold font-mono" />
                </div>
              )}

              <div>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">New Primary Slug</p>
                <div className="flex items-center gap-1 rounded-xl border border-slate-200 px-3 py-2 focus-within:ring-2 focus-within:ring-brand-primary/20 bg-slate-50/30">
                  <span className="text-xs font-bold text-slate-400 font-mono shrink-0">https://</span>
                  <input
                    value={slugValue}
                    onChange={e => { setSlugValue(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '')); if (slugError) setSlugError(''); }}
                    placeholder="acme"
                    className="flex-1 min-w-0 text-sm font-mono outline-none bg-transparent"
                  />
                  <span className="text-xs font-bold text-slate-400 font-mono shrink-0">.{getRootDomain()}</span>
                </div>
                {slugError && (
                  <p role="alert" className="mt-2 text-xs font-semibold text-red-600 bg-red-50 border border-red-100 rounded-lg px-2.5 py-2">{slugError}</p>
                )}
              </div>
            </div>

            <div className="flex gap-2 pt-1">
              <button onClick={() => setShowSlug(false)} className="flex-1 px-4 py-2.5 rounded-xl text-xs font-bold text-slate-600 bg-slate-50 border border-slate-100 hover:bg-slate-100 cursor-pointer">
                Cancel
              </button>
              <button onClick={handleChangeSlug} disabled={saving || !slugValue.trim()} className="flex-1 px-4 py-2.5 rounded-xl text-xs font-bold text-white btn-primary disabled:opacity-50 cursor-pointer flex items-center justify-center">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Set as Primary'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* LOGO MANAGEMENT & CROPPING MODAL */}
      {showLogo && activeWorkspace && (
        <Modal onClose={() => setShowLogo(false)} title="Workspace Logo & Cropper">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-6 space-y-4 animate-scaleIn">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-black text-slate-900 flex items-center gap-2">
                <Upload className="h-4 w-4 text-brand-primary" /> Workspace Logo & Cropper
              </h3>
              <button aria-label="Close" title="Close" onClick={() => setShowLogo(false)} className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-400 cursor-pointer">
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-4">
              <p className="text-xs text-slate-500 font-medium">Upload any PNG, JPG, WEBP, or SVG logo, then adjust scale and crop constraints below to format as a perfect workspace icon.</p>

              <div className="flex flex-col items-center justify-center border-2 border-dashed border-slate-200 rounded-2xl p-4 hover:bg-slate-50 transition-colors relative">
                {logoSourceUrl ? (
                  <div className="w-full space-y-3">
                    {/* Visual Cropper Display */}
                    <div className="relative w-full h-40 bg-slate-200 rounded-xl overflow-hidden flex items-center justify-center">
                      <img
                        ref={cropperImgRef}
                        src={logoSourceUrl}
                        alt="Original"
                        style={{
                          transform: `scale(${cropZoom}) translate(${cropX * 10}px, ${cropY * 10}px)`,
                          transition: 'transform 0.1s ease-out',
                          maxWidth: '100%',
                          maxHeight: '100%',
                          objectFit: 'contain'
                        }}
                      />
                      {/* Center Square Boundary overlay */}
                      <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                        <div className="w-32 h-32 border-2 border-dashed border-brand-primary rounded-2xl shadow-[0_0_0_1000px_rgba(0,0,0,0.5)]"></div>
                      </div>
                    </div>

                    {/* Canvas used for actual cropping export */}
                    <canvas ref={cropperCanvasRef} style={{ display: 'none' }} />

                    {/* Cropper controls */}
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-bold text-slate-400 uppercase w-12">Zoom</span>
                        <input
                          type="range"
                          min="1"
                          max="4"
                          step="0.05"
                          value={cropZoom}
                          onChange={e => setCropZoom(parseFloat(e.target.value))}
                          className="flex-1 accent-brand-primary"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-bold text-slate-400 uppercase w-12">Pan X</span>
                        <input
                          type="range"
                          min="-2"
                          max="2"
                          step="0.05"
                          value={cropX}
                          onChange={e => setCropX(parseFloat(e.target.value))}
                          className="flex-1 accent-brand-primary"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-bold text-slate-400 uppercase w-12">Pan Y</span>
                        <input
                          type="range"
                          min="-2"
                          max="2"
                          step="0.05"
                          value={cropY}
                          onChange={e => setCropY(parseFloat(e.target.value))}
                          className="flex-1 accent-brand-primary"
                        />
                      </div>
                    </div>
                  </div>
                ) : activeWorkspace.branding?.logoUrl ? (
                  <div className="flex flex-col items-center">
                    <img
                      src={activeWorkspace.branding.logoUrl}
                      alt="Current logo"
                      className="h-20 w-20 rounded-2xl border border-slate-100 object-contain bg-slate-50 shadow-sm"
                    />
                    <p className="text-[11px] font-bold text-slate-400 mt-2">Active Logo URL: {activeWorkspace.branding.logoUrl}</p>
                  </div>
                ) : (
                  <div className="h-20 w-20 rounded-2xl bg-slate-100 flex items-center justify-center text-slate-400">
                    <Building2 className="h-8 w-8" />
                  </div>
                )}

                {!logoSourceUrl && (
                  <div className="mt-3 text-center">
                    <span className="text-xs text-brand-primary font-bold hover:underline cursor-pointer block">Upload Image</span>
                    <p className="text-[11px] text-slate-500 mt-1">PNG, JPG, SVG, WEBP accepted</p>
                  </div>
                )}

                <input
                  type="file"
                  accept="image/png, image/jpeg, image/jpg, image/webp, image/svg+xml"
                  onChange={handleFileChange}
                  className={`absolute inset-0 opacity-0 cursor-pointer ${logoSourceUrl ? 'pointer-events-none h-0 w-0' : ''}`}
                />
              </div>

              {logoSourceUrl && (
                <div className="flex items-center justify-between gap-3 bg-slate-50 border border-slate-200 p-3 rounded-2xl shadow-inner">
                  <div className="text-[10px] font-bold text-slate-400 uppercase">Interactive Preview:</div>
                  {croppedBase64 && (
                    <img
                      src={croppedBase64}
                      alt="Cropped preview"
                      className="h-12 w-12 rounded-xl border border-slate-100 bg-white object-contain"
                    />
                  )}
                </div>
              )}
            </div>

            <div className="flex gap-2">
              <button
                onClick={handleLogoRemove}
                disabled={saving || (!activeWorkspace.branding?.logoUrl && !logoSourceUrl)}
                className="flex-1 px-3 py-2.5 rounded-xl text-xs font-bold text-rose-600 bg-rose-50 border border-rose-100 hover:bg-rose-100 cursor-pointer disabled:opacity-50"
              >
                Remove Logo
              </button>
              {logoSourceUrl ? (
                <button
                  onClick={handleLogoUpload}
                  disabled={saving || !croppedBase64}
                  className="flex-1 px-3 py-2.5 rounded-xl text-xs font-bold text-white btn-primary hover:shadow-lg cursor-pointer"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mx-auto" /> : 'Crop & Save'}
                </button>
              ) : (
                <button
                  onClick={() => setShowLogo(false)}
                  className="flex-1 px-3 py-2.5 rounded-xl text-xs font-bold text-slate-600 bg-slate-50 border border-slate-100 hover:bg-slate-100 cursor-pointer"
                >
                  Close
                </button>
              )}
            </div>
          </div>
        </Modal>
      )}

      {/* MANAGE USERS MODAL */}
      {showUsers && activeWorkspace && (
        <Modal onClose={() => setShowUsers(false)} title="Manage Workspace Users">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col animate-scaleIn">
            <div className="px-5 sm:px-6 py-4 border-b border-slate-100 bg-linear-to-r from-slate-50 to-white">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-black text-slate-900 flex items-center gap-2">
                  <Users className="h-5 w-5 text-brand-primary" /> Manage Workspace Users
                </h3>
                <p className="text-[11px] text-slate-500 font-bold mt-0.5">{activeWorkspace.name} · {workspaceUsers[activeWorkspace.slug]?.length ?? 0} total users</p>
              </div>
              <button aria-label="Close" title="Close" onClick={() => setShowUsers(false)} className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-400 cursor-pointer">
                <X className="h-4 w-4" />
              </button>
            </div>

            </div>

            <div className="flex-1 min-h-0 grid grid-cols-1 md:grid-cols-3 gap-0 md:gap-5 p-5 sm:p-6 overflow-y-auto">

              {/* User Invitation Column */}
              <div className="md:col-span-1 md:border-r border-slate-100 pr-0 md:pr-5 pb-5 md:pb-0 space-y-3">
                <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Invite Teammate</h4>
                <div className="space-y-2">
                  <input
                    value={inviteEmail}
                    onChange={e => setInviteEmail(e.target.value)}
                    placeholder="colleague@domain.com"
                    className="w-full border border-slate-200 rounded-xl px-3 py-2 text-xs bg-slate-50/50 outline-none focus:ring-2 focus:ring-brand-primary/20"
                  />
                  <select
                    value={inviteRole}
                    onChange={e => setInviteRole(e.target.value)}
                    className="w-full border border-slate-200 rounded-xl px-3 py-2 text-xs bg-white text-slate-600 outline-none"
                  >
                    <option value="member">Teammate</option>
                    <option value="admin">Admin</option>
                    <option value="manager">Manager</option>
                    <option value="employer">Employer</option>
                    <option value="sponsor">Sponsor</option>
                    <option value="referrer">Referrer</option>
                    <option value="client">Client</option>
                  </select>
                  <button onClick={handleInviteUser} className="w-full inline-flex items-center justify-center gap-1.5 py-2 rounded-xl btn-primary text-white text-xs font-bold cursor-pointer hover:shadow-md transition-all">
                    <UserPlus className="h-3.5 w-3.5" /> Invite Teammate
                  </button>
                </div>
              </div>

              {/* Users Directory List */}
              <div className="md:col-span-2 space-y-3 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Users Directory</h4>
                  <input
                    value={userSearch}
                    onChange={e => setUserSearch(e.target.value)}
                    placeholder="Search members..."
                    className="border border-slate-200 rounded-xl px-3 py-2 text-xs w-44 outline-none bg-slate-50/60 focus:ring-2 focus:ring-brand-primary/20"
                  />
                </div>

                <div className="max-h-[55vh] overflow-y-auto space-y-2 pr-1">
                  {(workspaceUsers[activeWorkspace.slug] || [])
                    .filter(u => !userSearch.trim() || u.email.toLowerCase().includes(userSearch.toLowerCase()) || u.name.toLowerCase().includes(userSearch.toLowerCase()))
                    .map(u => (
                      <div key={u.uid} className="group flex items-center justify-between gap-3 border border-slate-200 rounded-2xl p-3 bg-white hover:bg-slate-50 hover:border-brand-primary/20 hover:shadow-sm transition-all">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="h-10 w-10 rounded-2xl bg-brand-primary/10 text-brand-primary flex items-center justify-center overflow-hidden shrink-0 font-black text-xs ring-1 ring-brand-primary/10">
                            {u.profilePhotoUrl ? <img src={u.profilePhotoUrl} alt="" className="h-full w-full object-cover" /> : (u.name || u.email || '?').slice(0, 2).toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-black text-slate-900 truncate">{u.name || 'Unnamed user'}</p>
                            <p className="text-[11px] text-slate-500 truncate font-mono">{u.email}</p>
                            <div className="mt-1 flex items-center gap-1.5">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-black ${u.status === 'disabled' ? 'bg-rose-50 text-rose-700 border-rose-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}`}>{u.status === 'disabled' ? 'Disabled' : 'Active'}</span>
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[10px] font-black capitalize">{u.role || 'member'}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <select
                            value={u.role}
                            onChange={e => handleUpdateUserRole(u.uid, e.target.value)}
                            className="border border-slate-200 rounded-xl px-2.5 py-2 text-[11px] font-bold bg-white text-slate-700 outline-none focus:ring-2 focus:ring-brand-primary/20"
                          >
                            <option value="member">Teammate</option>
                            <option value="admin">Admin</option>
                            <option value="employer">Employer</option>
                            <option value="sponsor">Sponsor</option>
                            <option value="referrer">Referrer</option>
                            <option value="client">Client</option>
                            {(roles[activeWorkspace.slug] || []).map(r => (
                              <option key={r.roleId} value={r.roleId}>{r.name}</option>
                            ))}
                          </select>

                          <button
                            onClick={() => handleToggleUserBlock(u)}
                            className={`p-2 rounded-xl border cursor-pointer transition-all ${
                              u.status === 'disabled'
                                ? 'bg-rose-50 border-rose-200 text-rose-600 hover:bg-rose-100'
                                : 'bg-slate-50 border-slate-200 text-slate-400 hover:text-rose-600 hover:bg-rose-50'
                            }`}
                            title={u.status === 'disabled' ? 'Unlock account' : 'Lock account'}
                          >
                            <Ban className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                    ))}
                </div>
              </div>

            </div>
          </div>
        </Modal>
      )}

      {/* MANAGE CUSTOM ROLES MODAL */}
      {showRoles && activeWorkspace && (
        <Modal onClose={() => setShowRoles(false)} title="Workspace Custom Roles">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-3xl p-6 space-y-4 animate-scaleIn">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-black text-slate-900 flex items-center gap-2">
                  <UserCog className="h-5 w-5 text-brand-primary" /> Workspace Custom Roles
                </h3>
                <p className="text-[11px] text-slate-500 font-bold mt-0.5">{activeWorkspace.name} · Create up to {MAX_CUSTOM_ROLES} custom roles.</p>
              </div>
              <button aria-label="Close" title="Close" onClick={() => { setShowRoles(false); setShowTeamMemberPermissions(false); }} className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-400 cursor-pointer">
                <X className="h-4 w-4" />
              </button>
            </div>

            {showTeamMemberPermissions ? (
              <div className="space-y-4">
                <div className="flex items-start justify-between gap-4 rounded-2xl border border-indigo-100 bg-indigo-50/50 p-4">
                  <div><h4 className="text-sm font-black text-indigo-950">Team Member Permissions</h4><p className="text-[11px] text-indigo-700 mt-1">This built-in role applies to every Team Member in this workspace. Admins always retain full access and cannot be restricted here.</p></div>
                  <button onClick={() => setShowTeamMemberPermissions(false)} className="text-xs font-bold text-indigo-700 hover:underline">Back to roles</button>
                </div>
                <div className="max-h-[55vh] overflow-y-auto pr-1"><CustomRolePermissionsEditor roleName="Team Member" permissions={teamMemberPermissions} onChange={setTeamMemberPermissions} /></div>
                <div className="flex justify-end gap-2"><button onClick={() => setShowTeamMemberPermissions(false)} className="px-4 py-2 rounded-xl text-xs font-bold bg-slate-100 text-slate-700">Cancel</button><button onClick={saveTeamMemberPermissions} disabled={saving} className="px-4 py-2 rounded-xl text-xs font-bold text-white btn-primary disabled:opacity-50">{saving ? 'Saving…' : 'Save Team Member Permissions'}</button></div>
              </div>
            ) : <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

              {/* Roles Directory List */}
              <div className="border-r border-slate-100 pr-0 md:pr-4 space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Workspace Custom Roles</h4>
                  <button
                    onClick={openTeamMemberPermissions}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border border-indigo-200 bg-indigo-50 text-indigo-700 text-[10px] font-bold cursor-pointer"
                  ><Users className="h-3 w-3" /> Team Member Access</button>
                  <button
                    onClick={() => {
                      setEditingRole(null);
                      setRoleForm({
                        name: '',
                        description: '',
                        permissions: {
                          applicants: 'none', finance: 'none', jobs: 'none', employers: 'none', referrers: 'none',
                          sponsors: 'none', organizations: 'none', batch: 'none', events: 'none', reports: 'none',
                          approvals: 'none', settings: 'none', templates: 'none', 'doc-templates': 'none',
                          users: 'none', tickets: 'none', 'ai-suggestions': 'none', invitations: 'none'
                        }
                      });
                      setShowRoleForm(true);
                    }}
                    disabled={(roles[activeWorkspace.slug] || []).length >= MAX_CUSTOM_ROLES}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg btn-primary text-white text-[10px] font-bold cursor-pointer disabled:opacity-50"
                  >
                    <Plus className="h-3 w-3" /> New Role
                  </button>
                </div>

                <div className="max-h-80 overflow-y-auto space-y-2 pr-1">
                  {(roles[activeWorkspace.slug] || []).map(r => (
                    <div key={r.roleId} className="border border-slate-100 rounded-xl p-3 bg-slate-50/30 flex items-center justify-between gap-4">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <p className="text-xs font-black text-slate-800 truncate">{r.name}</p>
                          <span className={`text-[11px] font-bold px-1.5 py-0.5 rounded border capitalize ${r.archived ? 'bg-slate-100 text-slate-400' : 'bg-emerald-50 text-emerald-600 border-emerald-100'}`}>
                            {r.status}
                          </span>
                        </div>
                        <p className="text-[11px] text-slate-500 font-bold mt-1 line-clamp-1 italic">{r.description || 'No description provided.'}</p>
                        <p className="text-[11px] text-slate-500 font-bold mt-1">Assigned Users: {r.assignedCount} · Active Perms: {r.permissionCount}</p>
                      </div>

                      <div className="flex items-center gap-1 shrink-0">
                        <button
                          onClick={() => {
                            setEditingRole(r);
                            setRoleForm({ name: r.name, description: r.description || '', permissions: r.permissions });
                            setShowRoleForm(true);
                          }}
                          className="p-1.5 border border-slate-100 hover:bg-slate-50 rounded-lg text-slate-500 cursor-pointer"
                          title="Edit role permissions"
                        >
                          <Edit2 className="h-3 w-3" />
                        </button>
                        <button
                          onClick={() => handleDuplicateRole(r)}
                          className="p-1.5 border border-slate-100 hover:bg-slate-50 rounded-lg text-slate-500 cursor-pointer"
                          title="Duplicate role"
                        >
                          <Copy className="h-3 w-3" />
                        </button>
                        <button
                          onClick={() => handleDeleteRole(r.roleId)}
                          className="p-1.5 border border-slate-100 hover:bg-slate-50 rounded-lg text-rose-500 cursor-pointer"
                          title="Archive/Delete role"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </div>
                    </div>
                  ))}
                  {(roles[activeWorkspace.slug] || []).length === 0 && (
                    <p className="text-xs text-slate-500 italic py-6 text-center">No custom roles created. Build up to 5 custom permission sets.</p>
                  )}
                </div>
              </div>

              {/* Role Form / Permissions Editor Column */}
              {showRoleForm ? (
                <div className="space-y-3 animate-fadeIn">
                  <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                    {editingRole ? 'Edit Role Permissions' : 'Create Custom Role'}
                  </h4>

                  <div className="space-y-3 max-h-80 overflow-y-auto pr-1">
                    <div>
                      <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Role Name</label>
                      <input
                        value={roleForm.name}
                        onChange={e => setRoleForm(f => ({ ...f, name: e.target.value }))}
                        placeholder="Managers"
                        className="w-full border border-slate-200 rounded-xl px-3 py-2 text-xs bg-slate-50/50 outline-none"
                      />
                    </div>

                    <div>
                      <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Role Description</label>
                      <textarea
                        value={roleForm.description}
                        onChange={e => setRoleForm(f => ({ ...f, description: e.target.value }))}
                        placeholder="Short explanation of who this role is for and their responsibilities..."
                        rows={2}
                        className="w-full border border-slate-200 rounded-xl px-3 py-2 text-xs bg-slate-50/50 outline-none resize-none font-medium text-slate-700"
                      />
                    </div>

                    <div className="border border-slate-200 rounded-2xl p-3 space-y-2 bg-white">
                      <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Role Module Groups & Permissions</p>
                      <CustomRolePermissionsEditor
                        roleName={roleForm.name || 'Custom Role'}
                        permissions={roleForm.permissions || {}}
                        onChange={updatedPerms => setRoleForm(f => ({ ...f, permissions: updatedPerms }))}
                      />
                    </div>
                  </div>

                  <div className="flex gap-2 pt-1 border-t border-slate-100">
                    <button onClick={() => { setShowRoleForm(false); setEditingRole(null); }} className="flex-1 py-2 rounded-xl text-xs font-bold text-slate-600 bg-slate-50 border border-slate-100 hover:bg-slate-100 cursor-pointer">
                      Cancel
                    </button>
                    <button onClick={handleSaveRole} disabled={saving} className="flex-1 py-2 rounded-xl text-xs font-bold text-white btn-primary hover:shadow-lg cursor-pointer">
                      {saving ? <Loader2 className="h-4 w-4 animate-spin mx-auto" /> : 'Save Role'}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center p-6 text-center text-slate-400 h-full">
                  <UserCog className="h-10 w-10 text-slate-300 stroke-1 mb-2" />
                  <p className="text-xs font-bold text-slate-500">No Role Selected</p>
                  <p className="text-[11px] text-slate-500 mt-1 max-w-xs">Select edit on an existing role or click New Role to customize a set of access permissions.</p>
                </div>
              )}

            </div>}
          </div>
        </Modal>
      )}

      {/* AUDIT LOGS MODAL */}
      {showAudit && activeWorkspace && (
        <Modal onClose={() => setShowAudit(false)} title="Platform Workspace Logs">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-xl p-6 space-y-4 animate-scaleIn">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-black text-slate-900 flex items-center gap-2">
                  <Clock className="h-5 w-5 text-brand-primary" /> Platform Workspace Logs
                </h3>
                <p className="text-[11px] text-slate-500 font-bold mt-0.5">Historical audit trail of super-administrator workspace modifications.</p>
              </div>
              <button aria-label="Close" title="Close" onClick={() => setShowAudit(false)} className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-400 cursor-pointer">
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="max-h-96 overflow-y-auto space-y-3 pr-1 font-sans">
              {(auditLogs[activeWorkspace.slug] || []).length === 0 ? (
                <p className="text-xs text-slate-500 italic py-12 text-center">No log actions recorded for this workspace.</p>
              ) : (
                (auditLogs[activeWorkspace.slug] || []).map(log => (
                  <div key={log.id} className="border border-slate-100 rounded-xl p-3 bg-slate-50/20 space-y-1">
                    <div className="flex items-center justify-between gap-2 text-[10px] font-bold">
                      <span className="text-indigo-600 bg-indigo-50 border border-indigo-100 px-1.5 py-0.5 rounded uppercase">
                        {log.action}
                      </span>
                      <span className="text-slate-400">
                        {new Date(log.timestamp || '').toLocaleString()}
                      </span>
                    </div>
                    <p className="text-xs font-semibold text-slate-700 pt-1 leading-snug">{log.details}</p>
                    <p className="text-[11px] text-slate-500 font-medium">Performed by: {log.changedBy || log.adminName}</p>
                  </div>
                ))
              )}
            </div>
          </div>
        </Modal>
      )}

      {/* BILLING INFO MODAL */}
      {showBillingInfo && activeWorkspace && (
        <Modal onClose={() => setShowBillingInfo(false)} title="Billing & Subscription">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-sm p-6 space-y-4 animate-scaleIn">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-black text-slate-900 flex items-center gap-2">
                <Database className="h-4 w-4 text-brand-primary" /> Billing & Subscription
              </h3>
              <button aria-label="Close" title="Close" onClick={() => setShowBillingInfo(false)} className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-400 cursor-pointer">
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-4 text-xs font-semibold text-slate-600">
              <p className="text-slate-500">Plan details, seat allocations, and payment limits for {activeWorkspace.name}.</p>
              <div className="border border-slate-100 rounded-2xl p-4 space-y-2 bg-slate-50/20">
                <div className="flex justify-between">
                  <span>Current Tier:</span>
                  <span className="font-black text-slate-800 uppercase">{activeWorkspace.plan}</span>
                </div>
                <div className="flex justify-between">
                  <span>Workspace Status:</span>
                  <span className="text-emerald-600 font-bold capitalize">{activeWorkspace.status}</span>
                </div>
                <div className="flex justify-between">
                  <span>Monthly Rate:</span>
                  <span className="text-slate-800">
                    {activeWorkspace.plan === 'free' ? '$0' : activeWorkspace.plan === 'starter' ? '$49' : activeWorkspace.plan === 'growth' ? '$149' : activeWorkspace.plan === 'scale' ? '$499' : 'Custom Enterprise Quote'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Billing Cycle:</span>
                  <span className="text-slate-800">Monthly</span>
                </div>
              </div>

              <div>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">Change Subscription Tier</p>
                <div className="flex gap-2">
                  <select
                    defaultValue={activeWorkspace.plan}
                    onChange={(e) => updateWorkspaceProperty(activeWorkspace.slug, { plan: e.target.value })}
                    className="flex-1 border border-slate-200 rounded-xl px-3 py-2 text-xs bg-white text-slate-600 outline-none"
                  >
                    {PLANS.map(p => <option key={p} value={p}>{p.toUpperCase()}</option>)}
                  </select>
                </div>
              </div>
            </div>

            <div className="pt-2">
              <button onClick={() => setShowBillingInfo(false)} className="w-full py-2.5 rounded-xl text-xs font-bold text-slate-600 bg-slate-50 border border-slate-100 hover:bg-slate-100 cursor-pointer">
                Close
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* DOUBLE CONFIRMATION DELETE WORKSPACE MODAL */}
      {showDeleteConfirm && activeWorkspace && (
        <Modal onClose={() => setShowDeleteConfirm(false)} title="Protected Super-Admin Action">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-sm p-6 space-y-4 animate-scaleIn">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-black text-rose-700 flex items-center gap-2">
                <ShieldAlert className="h-5 w-5 text-rose-500 animate-bounce" /> Protected Super-Admin Action
              </h3>
              <button aria-label="Close" title="Close" onClick={() => setShowDeleteConfirm(false)} className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-400 cursor-pointer">
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-3">
              <p className="text-xs text-slate-500 font-medium">
                This is a highly destructive action! Deleting workspace <strong>{activeWorkspace.name}</strong> will deactivate its custom domains, disable portals, and soft-archive all of its data.
              </p>

              <div>
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-2">Type <span className="font-mono text-rose-600 font-extrabold">DELETE</span> to confirm:</p>
                <input
                  value={deleteMatchInput}
                  onChange={e => setDeleteMatchInput(e.target.value)}
                  placeholder="DELETE"
                  className="w-full border border-rose-200 rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-rose-500/20 bg-rose-50/5 text-rose-700 font-mono font-black"
                />
              </div>
            </div>

            <div className="flex gap-2">
              <button onClick={() => { setShowDeleteConfirm(false); setDeleteMatchInput(''); }} className="flex-1 px-4 py-2.5 rounded-xl text-xs font-bold text-slate-600 bg-slate-50 border border-slate-100 hover:bg-slate-100 cursor-pointer">
                Cancel
              </button>
              <button
                onClick={handleDeleteWorkspace}
                disabled={saving || deleteMatchInput !== 'DELETE'}
                className="flex-1 px-4 py-2.5 rounded-xl text-xs font-bold text-white bg-rose-600 hover:bg-rose-700 shadow-md shadow-rose-500/15 cursor-pointer disabled:opacity-40"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin mx-auto" /> : 'Archive Tenant'}
              </button>
            </div>
          </div>
        </Modal>
      )}

    </div>
  );
}
